import { useEffect, useState } from 'react';
import { Copy, ExternalLink, Link2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  COLLECTION_LABELS,
  contentAccessUrl,
  type ContentIndexItem,
} from '../../lib/contentIndex';
import { ensureSubscriberManageToken } from '../../lib/subscriberManageToken';
import RecommendContentPickerModal from './RecommendContentPickerModal';

interface TestContentLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriberId: string | number;
  subscriberEmail?: string | null;
  subscriberName?: string | null;
  emailStatus?: string | null;
  manageToken?: string | null;
  manageTokenExpiresAt?: string | null;
  onTokenMinted?: () => void;
}

export default function TestContentLinkModal({
  isOpen,
  onClose,
  subscriberId,
  subscriberEmail,
  subscriberName,
  emailStatus: _emailStatus,
  manageToken,
  manageTokenExpiresAt,
  onTokenMinted,
}: TestContentLinkModalProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [item, setItem] = useState<ContentIndexItem | null>(null);
  const [accessUrl, setAccessUrl] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const displayName = (subscriberName ?? subscriberEmail ?? 'subscriber').trim();

  useEffect(() => {
    if (!isOpen) return;
    setPickerOpen(false);
    setItem(null);
    setAccessUrl(null);
    setGenerating(false);
    setCopied(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !generating && !pickerOpen) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, generating, pickerOpen]);

  async function buildLink(picked: ContentIndexItem) {
    setGenerating(true);
    setItem(picked);
    setAccessUrl(null);
    setCopied(false);
    try {
      const token = await ensureSubscriberManageToken({
        id: subscriberId,
        manage_token: manageToken,
        manage_token_expires_at: manageTokenExpiresAt,
      });
      setAccessUrl(contentAccessUrl(picked.url, token));
      onTokenMinted?.();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not mint access token';
      toast.error(msg);
      setItem(null);
    } finally {
      setGenerating(false);
    }
  }

  async function copyLink() {
    if (!accessUrl) return;
    try {
      await navigator.clipboard.writeText(accessUrl);
      setCopied(true);
      toast.success('Link copied');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  }

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-black/50">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="test-content-link-title"
          className="w-full max-w-lg bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        >
          <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
            <div>
              <h2
                id="test-content-link-title"
                className="text-lg font-semibold text-gray-900 dark:text-white"
              >
                Test content link
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Same <code className="text-xs">?token=</code> URL as recommendation emails — for{' '}
                <span className="font-medium text-gray-700 dark:text-gray-300">{displayName}</span>.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-4 overflow-y-auto">
            {!item ? (
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                disabled={generating}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg border border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 disabled:opacity-50"
              >
                <Link2 className="w-4 h-4" />
                Pick a content page
              </button>
            ) : (
              <div className="space-y-3">
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{item.title}</div>
                  <div className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
                    {COLLECTION_LABELS[item.collection]}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setItem(null);
                      setAccessUrl(null);
                      setPickerOpen(true);
                    }}
                    className="mt-2 text-xs text-emerald-700 dark:text-emerald-300 hover:underline"
                  >
                    Choose a different page
                  </button>
                </div>

                {generating ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Minting access token…
                  </div>
                ) : accessUrl ? (
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                      One-click login URL
                    </label>
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2 font-mono text-[11px] text-gray-700 dark:text-gray-300 break-all">
                      {accessUrl}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => void copyLink()}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <Copy className="w-4 h-4" />
                        {copied ? 'Copied' : 'Copy link'}
                      </button>
                      <a
                        href={accessUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open as subscriber
                      </a>
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      Opens stephansmith.me signed in as this subscriber — use an incognito window
                      if you are already logged in on the site.
                    </p>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <div className="flex justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <RecommendContentPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onAdd={(items) => {
          const first = items[0];
          if (first) void buildLink(first);
        }}
        maxSelections={1}
        title="Pick page to test"
        confirmLabel="Generate link"
      />
    </>
  );
}
